<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Commission Check Report</h1>
            <a :href="route('frontend.reports.finance_commission_check_report_export', {
                _query: {
                    supplier_ids: form.filter.supplier,
                    starts_at: form.filter.starts_at,
                    ends_at: form.filter.ends_at,
                    trading_entity: form.filter.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">
                Export
            </a>
        </div>
    </div>
    <br/>
    <div class="grid grid-cols-4 gap-2">
        <div class="relative">
            <label for="supplier" class="block text-sm font-medium text-gray-700">Supplier</label>
            <v-select class="mt-3 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="suppliers" label="name" :reduce="suppliers => suppliers.id" v-model="form.filter.supplier" placeholder="Supplier" :multiple="true" />
        </div>
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.filter.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.filter.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
            <input v-model="form.filter.starts_at" id="start-date" type="date" class="mt-2 bg-white border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
            <input v-model="form.filter.ends_at" id="end-date" type="date" class="mt-2 bg-white border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
        </div>
    </div>
    <br/>
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Supplier
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Code
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Commission Rate
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Pitch
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="Object.values(grouped_services_filtered).length > 0">
                                    <template v-for="(dataset, key) in grouped_services_filtered">
                                        <tr>
                                            <td colspan="2" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                {{ key }}
                                            </td>
                                            <td colspan="2" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                {{ dataset.commission_rate_percent ?? 'Not Set' }}
                                            </td>
                                        </tr>
                                        <template v-for="(data, index) in dataset">
                                            <template v-if="index !== 'commission_rate_percent'">
                                                <template v-for="items in data">
                                                    <template v-for="item in items">
                                                        <tr>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ item.fixture_title }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ item.fixture_code }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ item.commission_rate }}% {{ item.commission_type }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <a :href="route('frontend.fixture.show', item.fixture_id)" class="font-medium text-pitch-purple hover:text-light-pitch-purple" target="_blank"> {{ route('frontend.fixture.show', item.fixture_id) }} </a>
                                                            </td>
                                                        </tr>
                                                    </template>
                                                </template>
                                            </template>
                                        </template>
                                    </template>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td colspan="4" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            Your search criteria produces no results.
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import mapValues from "lodash/mapValues";
    import debounce from "lodash/debounce";
    import pickBy from "lodash/pickBy";
    import SearchFilter from "@/Components/SearchFilter";
    import moment from "moment/moment";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link
        },
        props: [
            'suppliers',
            'starts_at',
            'ends_at',
            'grouped_services_filtered',
            'trading_details'
        ],
        data(props) {
            return {
                form: useForm({
                    search: null,
                    filter: {
                        supplier: props.selected_suppliers ?? '',
                        starts_at: props.starts_at,
                        ends_at: props.ends_at,
                        trading_entity: null
                    }
                })
            }
        },
        computed: {
            formattedStartDate() {
                if (this.form.filter.starts_at) {
                    return moment(this.form.filter.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.filter.ends_at) {
                    return moment(this.form.filter.ends_at).format('DD MMM YYYY');
                }
                return '';
            }
        },
        methods: {
            reset() {
                this.form = mapValues(this.form, () => null)
            },
        },
        created() {
            this.moment = moment
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.finance_commission_check_report'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            }
        }
    }
</script>
